import { create } from 'zustand';
import type {
  IdentityId,
  DeviceId,
  PublicKey,
  TemporaryContactRecord,
  TemporaryMembershipRecord
} from '@shared/types';
import {
  loadMasterKey,
  loadAllIdentities,
  loadAllDevices,
  loadAllTemporaryMemberships,
  saveIdentity,
  saveDevice,
  deleteIdentity,
  saveTemporaryMembership,
  deleteTemporaryMembership,
  unwrapIdentityPrivateKey,
  unwrapDeviceEcdhScalar,
  migrateDeviceEcdhScalarToWrapped,
  wrapIdentityPrivateKeyForSession,
  type WrappedKey
} from '../crypto/storage';
import { computeFingerprint, deriveVaultKey } from '../crypto/keys';
import { normalizeServerUrl, serverKeyFromServerUrl } from '../utils/serverKey';
import { createDeviceCryptoKeys, hasEd25519SigningSupport, type SigningKey } from '../crypto/ed25519Provider';
import { base64ToBytes, toArrayBuffer } from '../utils/encoding';
import { isWebViewMode, loadKeysFromBridge, decodeBridgeData, androidHasStoredKeys, updateBridgeIdentity } from '../utils/webviewBridge';
import type { IdentityAccessKind } from '../utils/identityAccess';

export interface Identity {
  identityId: IdentityId;
  // Client-wide server key: derived from server URL (scheme + host + port)
  serverId: string;
  serverName: string;
  serverUrl?: string;
  identityName?: string;
  publishIdentity?: boolean;
  presenceVisible?: boolean;
  noNamesOnServer?: boolean;
  publicKey: PublicKey;
  fingerprint: string;
  role?: string;
  accessKind?: IdentityAccessKind;
  accessContextLabel?: string;
  vpsId?: string;
  isServerAdmin?: boolean;
  isPrimaryVpsTarget?: boolean;
  signingKey: SigningKey;
  // X25519 ECDH CryptoKey — null on browsers that do not support WebCrypto X25519
  // (Chrome < 133, Firefox < 130). Use ecdhScalar + noble as fallback in that case.
  ecdhKey: CryptoKey | null;
  ecdhScalar: Uint8Array;
  vaultKey: CryptoKey;
  // Raw private key bytes encrypted with localWrappingKey.
  // Used only when raw bytes are needed (e.g. trusted-device transfer payload).
  wrappedPrivateKey: WrappedKey;
  useForBackupStorage?: boolean;
}

export interface Device {
  deviceId: DeviceId;
  identityId: IdentityId;
  /** Ed25519 signing public key. */
  publicKey: PublicKey;
  /** X25519 encryption public key. Missing for legacy devices. */
  encryptionPublicKey?: PublicKey | null;
  label?: string;
  signingKey: SigningKey;
  ecdhKey: CryptoKey | null;
  ecdhScalar: Uint8Array;
}

export interface TemporaryMembership extends TemporaryMembershipRecord {
  /** Ed25519 signing private key bytes. */
  privateKeyBytes: Uint8Array;
  /** X25519 encryption private key bytes. Missing on legacy temporary memberships. */
  encryptionPrivateKeyBytes?: Uint8Array;
  privateKeyJwk?: JsonWebKey;
  signingKey: SigningKey;
  ecdhKey: CryptoKey | null;
  ecdhScalar: Uint8Array;
  cachedContacts?: TemporaryContactRecord[];
}

type TemporaryMembershipDraft =
  Omit<TemporaryMembership, 'signingKey' | 'ecdhKey' | 'ecdhScalar'>
  & Partial<Pick<TemporaryMembership, 'signingKey' | 'ecdhKey' | 'ecdhScalar'>>;

interface AuthState {
  // Master key
  masterKey: CryptoKey | null;
  hasMasterKey: boolean;

  // Identities (a full Circle identity and zero or more guest identities may share a server)
  identities: Record<IdentityId, Identity>;

  // Devices
  devices: Record<DeviceId, Device>;
  currentDeviceId: DeviceId | null;
  temporaryMemberships: Record<string, TemporaryMembership>;

  // Loading state
  isLoading: boolean;
  isInitialized: boolean;

  // Set when a fatal browser-level error prevents the app from starting.
  initError: 'ed25519_not_supported' | 'android_keys_missing' | null;

  // Actions
  setMasterKey: (key: CryptoKey) => void;
  clearMasterKey: () => void;

  addIdentity: (identity: Identity) => void;
  updateIdentity: (identityId: IdentityId, updates: Partial<Identity>) => void;
  removeIdentity: (identityId: IdentityId) => void;

  addDevice: (device: Device) => void;
  removeDevice: (deviceId: DeviceId) => void;
  setCurrentDevice: (deviceId: DeviceId) => void;
  addTemporaryMembership: (membership: TemporaryMembershipDraft) => Promise<void>;
  removeTemporaryMembership: (enrollmentId: string) => Promise<void>;
  setTemporaryMembershipContacts: (enrollmentId: string, contacts: TemporaryContactRecord[]) => Promise<void>;

  initialize: () => Promise<void>;
  logout: () => void;
}

function toStoredTemporaryMembership(membership: TemporaryMembership) {
  const stored = { ...membership };
  delete (stored as Partial<TemporaryMembership>).signingKey;
  delete (stored as Partial<TemporaryMembership>).ecdhKey;
  delete (stored as Partial<TemporaryMembership>).ecdhScalar;
  return stored;
}

/**
 * Test whether WebCrypto supports Ed25519 key operations.
 * Required for identity signing and ECDH key derivation.
 */
async function checkEd25519Support(): Promise<boolean> {
  return hasEd25519SigningSupport();
}

export const useAuthStore = create<AuthState>((set) => ({
  masterKey: null,
  hasMasterKey: false,

  identities: {},

  devices: {},
  currentDeviceId: null,
  temporaryMemberships: {},

  isLoading: false,
  isInitialized: false,
  initError: null,

  setMasterKey: (key) => set({
    masterKey: key,
    hasMasterKey: true
  }),

  clearMasterKey: () => set({
    masterKey: null,
    hasMasterKey: false
  }),

  addIdentity: (identity) => set((state) => ({
    identities: {
      ...state.identities,
      [identity.identityId]: identity
    }
  })),

  updateIdentity: (identityId, updates) => set((state) => ({
    identities: {
      ...state.identities,
      [identityId]: {
        ...state.identities[identityId],
        ...updates
      }
    }
  })),

  removeIdentity: (identityId) => set((state) => {
    const newIdentities = { ...state.identities };
    delete newIdentities[identityId];

    // Remove devices that belong to this identity to avoid orphaned currentDeviceId.
    const newDevices = { ...state.devices };
    for (const [deviceId, device] of Object.entries(state.devices)) {
      if (device.identityId === identityId) {
        delete newDevices[deviceId as DeviceId];
      }
    }

    // If current device was removed, switch to first available.
    const currentDeviceStillExists = state.currentDeviceId ? !!newDevices[state.currentDeviceId] : false;
    const newCurrentDeviceId = currentDeviceStillExists
      ? state.currentDeviceId
      : ((Object.keys(newDevices)[0] as DeviceId | undefined) || null);

    return {
      identities: newIdentities,
      devices: newDevices,
      currentDeviceId: newCurrentDeviceId
    };
  }),

  addDevice: (device) => set((state) => ({
    devices: {
      ...state.devices,
      [device.deviceId]: device
    },
    currentDeviceId: device.deviceId
  })),

  removeDevice: (deviceId) => set((state) => {
    const newDevices = { ...state.devices };
    delete newDevices[deviceId];

    // If removing current device, switch to first available
    const newCurrentDeviceId =
      state.currentDeviceId === deviceId
        ? Object.keys(newDevices)[0] || null
        : state.currentDeviceId;

    return {
      devices: newDevices,
      currentDeviceId: newCurrentDeviceId
    };
  }),

  setCurrentDevice: (deviceId) => set({
    currentDeviceId: deviceId
  }),

  addTemporaryMembership: async (membership) => {
    let resolvedMembership: TemporaryMembership;
    if (membership.signingKey && membership.ecdhScalar) {
      resolvedMembership = {
        ...membership,
        signingKey: membership.signingKey,
        ecdhKey: membership.ecdhKey ?? null,
        ecdhScalar: membership.ecdhScalar
      };
    } else {
      const { signingKey, ecdhKey, ecdhScalar } = await createDeviceCryptoKeys(
        membership.privateKeyBytes,
        base64ToBytes(membership.publicKey.value),
        membership.encryptionPrivateKeyBytes
      );
      resolvedMembership = {
        ...membership,
        signingKey,
        ecdhKey,
        ecdhScalar
      };
    }

    await saveTemporaryMembership(toStoredTemporaryMembership(resolvedMembership));
    set((state) => ({
      temporaryMemberships: {
        ...state.temporaryMemberships,
        [resolvedMembership.enrollmentId]: resolvedMembership
      }
    }));
    // Fire-and-forget: upload backup to all backup-storage circles.
    const _mk = useAuthStore.getState().masterKey;
    if (_mk) {
      import('../utils/circleBackup').then(({ uploadCircleBackup }) => {
        const s = useAuthStore.getState();
        uploadCircleBackup({ ...s, masterKey: _mk }).catch(() => {});
      }).catch(() => {});
    }
  },

  removeTemporaryMembership: async (enrollmentId) => {
    await deleteTemporaryMembership(enrollmentId);
    set((state) => {
      const next = { ...state.temporaryMemberships };
      delete next[enrollmentId];
      return { temporaryMemberships: next };
    });
  },

  setTemporaryMembershipContacts: async (enrollmentId, contacts) => {
    const state = useAuthStore.getState();
    const membership = state.temporaryMemberships[enrollmentId];
    if (!membership) return;

    const nextMembership: TemporaryMembership = {
      ...membership,
      cachedContacts: contacts
    };

    await saveTemporaryMembership(toStoredTemporaryMembership(nextMembership));
    set((current) => ({
      temporaryMemberships: {
        ...current.temporaryMemberships,
        [enrollmentId]: nextMembership
      }
    }));
  },

  initialize: async () => {
    set({ isLoading: true });

    try {
      // Check for required WebCrypto capabilities before doing any key work.
      const ed25519Ok = await checkEd25519Support();
      if (!ed25519Ok) {
        console.error('[Auth] FATAL: WebCrypto does not support Ed25519. Browser is not supported.');
        set({ isLoading: false, isInitialized: true, initError: 'ed25519_not_supported' });
        return;
      }

      // WebView mode: load master key + identity/device seeds strictly from Android bridge.
      // IndexedDB is still used for non-secret metadata and temporary memberships.
      // If the bridge has no keys and there is no local metadata either, treat it as a fresh install
      // so the normal first-run flow can create/import a profile.
      if (isWebViewMode) {
        const bridgeData = loadKeysFromBridge();
        if (!bridgeData) {
          const [storedIdentities, storedTemporaryMemberships] = await Promise.all([
            loadAllIdentities(),
            loadAllTemporaryMemberships()
          ]);
          const hasStoredIdentityMetadata = Object.keys(storedIdentities).length > 0;
          const hasStoredTemporaryMemberships = Object.keys(storedTemporaryMemberships).length > 0;

          // If Android keystore truly has no saved keys, treat as fresh install regardless
          // of IndexedDB state. Stale IndexedDB entries (e.g. from profiles created before
          // the flushToBridge fix) are unusable without seeds and can be safely ignored.
          const androidHasKeys = androidHasStoredKeys();

          if (!hasStoredIdentityMetadata && !hasStoredTemporaryMemberships) {
            set({
              masterKey: null,
              hasMasterKey: false,
              identities: {},
              devices: {},
              currentDeviceId: null,
              temporaryMemberships: {},
              isLoading: false,
              isInitialized: true,
              initError: null
            });
            return;
          }

          if (!androidHasKeys) {
            set({
              masterKey: null,
              hasMasterKey: false,
              identities: {},
              devices: {},
              currentDeviceId: null,
              temporaryMemberships: {},
              isLoading: false,
              isInitialized: true,
              initError: null
            });
            return;
          }

          console.error('[Auth] WebView mode: local metadata exists but Android bridge did not provide key material.');
          set({ isLoading: false, isInitialized: true, initError: 'android_keys_missing' });
          return;
        }

        const { masterKeyBytes, identitySeeds, deviceSeeds } = decodeBridgeData(bridgeData);
        const masterKey = masterKeyBytes
          ? await crypto.subtle.importKey(
            'raw',
            toArrayBuffer(masterKeyBytes),
            { name: 'AES-GCM', length: 256 },
            true,
            ['encrypt', 'decrypt']
          )
          : null;

        // All identity metadata is now stored in the bridge. IndexedDB is no longer
        // read for metadata. storedIdentities was removed — bridge is the sole source.

        const identities: Record<IdentityId, Identity> = {};

        for (const bridgeIdentity of bridgeData.identities) {
          if (bridgeIdentity.pendingRegistration) continue;
          if (!bridgeIdentity.publicKey) continue;
          const canonicalIdentityId = bridgeIdentity.id as IdentityId;
          const seed = identitySeeds.get(canonicalIdentityId);
          if (!seed) {
            continue;
          }
          try {
            const normalizedUrl = normalizeServerUrl(bridgeIdentity.serverUrl || '');
            const serverKey = serverKeyFromServerUrl(normalizedUrl) || '';
            const publicKey = { algorithm: 'ed25519' as const, value: bridgeIdentity.publicKey };

            const serverId = bridgeIdentity.serverId || serverKey || '';
            const serverName = bridgeIdentity.serverName ?? '';
            const identityName = bridgeIdentity.identityName;
            const publishIdentity = bridgeIdentity.publishIdentity;
            const presenceVisible = bridgeIdentity.presenceVisible;
            const role = bridgeIdentity.role;
            const accessKind = bridgeIdentity.accessKind;
            const accessContextLabel = bridgeIdentity.accessContextLabel;
            const vpsId = bridgeIdentity.vpsId;
            const isServerAdmin = bridgeIdentity.isServerAdmin ?? false;
            const isPrimaryVpsTarget = bridgeIdentity.isPrimaryVpsTarget ?? false;
            const noNamesOnServer = bridgeIdentity.noNamesOnServer ?? false;
            const useForBackupStorage = bridgeIdentity.useForBackupStorage ?? false;
            const fingerprint = bridgeIdentity.fingerprint || await computeFingerprint(publicKey);

            // Back-fill missing fields into the bridge (e.g. old app version that
            // didn't store fingerprint/serverId) so subsequent startups use bridge exclusively.
            if (!bridgeIdentity.fingerprint || !bridgeIdentity.serverId) {
              updateBridgeIdentity(canonicalIdentityId, seed, {
                publicKey: publicKey.value,
                serverUrl: normalizedUrl || '',
                serverName,
                identityName,
                publishIdentity,
                presenceVisible,
                role,
                accessKind,
                accessContextLabel,
                vpsId,
                isServerAdmin,
                isPrimaryVpsTarget,
                fingerprint,
                serverId,
                noNamesOnServer,
                useForBackupStorage,
              });
            }

            const publicKeyBytes = base64ToBytes(publicKey.value);
            const { signingKey, ecdhKey, ecdhScalar } = await createDeviceCryptoKeys(seed, publicKeyBytes);
            const vaultKey = await deriveVaultKey(seed);
            const wrappedPrivateKey: WrappedKey = await wrapIdentityPrivateKeyForSession(seed);
            identities[canonicalIdentityId] = {
              identityId: canonicalIdentityId,
              serverId,
              serverName,
              serverUrl: normalizedUrl,
              identityName,
              publishIdentity,
              presenceVisible,
              noNamesOnServer,
              publicKey,
              fingerprint,
              role,
              accessKind,
              accessContextLabel,
              vpsId,
              isServerAdmin,
              isPrimaryVpsTarget,
              signingKey,
              ecdhKey,
              ecdhScalar,
              vaultKey,
              wrappedPrivateKey,
              useForBackupStorage,
            };
          } catch (e) {
            console.error(`[Auth] WebView: failed to derive keys for identity ${canonicalIdentityId}:`, e);
          }
        }

        const devices: Record<DeviceId, Device> = {};
        if (deviceSeeds.size > 0) {
          for (const [deviceId, data] of deviceSeeds.entries()) {
            if (data.pendingRegistration) continue;
            try {
              const publicKey = { algorithm: 'ed25519' as const, value: data.publicKey };
              const { signingKey, ecdhKey, ecdhScalar } = await createDeviceCryptoKeys(
                data.seed,
                base64ToBytes(data.publicKey),
                data.encryptionSeed
              );
              devices[deviceId as DeviceId] = {
                deviceId: deviceId as DeviceId,
                identityId: data.identityId as IdentityId,
                publicKey,
                encryptionPublicKey: data.encryptionPublicKey ? { algorithm: 'x25519' as const, value: data.encryptionPublicKey } : null,
                label: data.label,
                signingKey,
                ecdhKey,
                ecdhScalar
              };
            } catch (e) {
              console.error(`[Auth] WebView: device ${deviceId} derive failed — skipping.`, e);
            }
          }
        } else {
          const storedDevices = await loadAllDevices();
          for (const [id, data] of Object.entries(storedDevices)) {
            if (data.pendingRegistration) continue;
            if (data.signingKey && data.wrappedEcdhScalar) {
              try {
                const ecdhScalar = await unwrapDeviceEcdhScalar(data.wrappedEcdhScalar, data.deviceId);
                let ecdhKey: CryptoKey | null = null;
                try {
                  ecdhKey = await crypto.subtle.importKey('raw', toArrayBuffer(ecdhScalar), { name: 'X25519' }, false, ['deriveBits']);
                } catch { /* noble fallback */ }
                devices[id] = { deviceId: data.deviceId, identityId: data.identityId, publicKey: data.publicKey, encryptionPublicKey: data.encryptionPublicKey ?? null, signingKey: data.signingKey, ecdhKey, ecdhScalar };
              } catch (e) {
                console.error(`[Auth] WebView: legacy IndexedDB device ${data.deviceId} unwrap failed — skipping.`, e);
              }
            }
          }
        }

        const storedTemporaryMemberships = await loadAllTemporaryMemberships();
        const temporaryMemberships: Record<string, TemporaryMembership> = {};
        const nowIso = Date.now();
        for (const [enrollmentId, membership] of Object.entries(storedTemporaryMemberships)) {
          const expiresAt = new Date(membership.expiresAt).getTime();
          if (!Number.isFinite(expiresAt) || expiresAt <= nowIso) continue;
          try {
            const { signingKey, ecdhKey, ecdhScalar } = await createDeviceCryptoKeys(
              membership.privateKeyBytes,
              base64ToBytes(membership.publicKey.value),
              membership.encryptionPrivateKeyBytes
            );
            temporaryMemberships[enrollmentId] = {
              ...membership,
              signingKey,
              ecdhKey,
              ecdhScalar
            };
          } catch (e) {
            console.error(`[Auth] WebView: temporary membership ${membership.enrollmentId} derive failed — skipping.`, e);
          }
        }

        const firstIdentityId = (Object.keys(identities)[0] as IdentityId | undefined) || null;
        const firstDeviceId = (firstIdentityId
          ? Object.values(devices).find((d) => d.identityId === firstIdentityId)?.deviceId
          : null) || (Object.keys(devices)[0] as DeviceId | undefined) || null;

        set({
          masterKey,
          hasMasterKey: !!masterKey,
          identities,
          devices,
          currentDeviceId: firstDeviceId,
          temporaryMemberships,
          isLoading: false,
          isInitialized: true
        });
        return;
      }

      const masterKey = await loadMasterKey();

      // Load identities/devices regardless of master key.
      // This allows the UI to know whether this browser already has an identity,
      // even when the master key is missing (e.g. cleared storage / new session).
      const storedIdentities = await loadAllIdentities();

      const identities: Record<IdentityId, Identity> = {};
      type MigrationEntry = { fromKey: string; toKey: IdentityId; identity: Identity };
      const migrations: MigrationEntry[] = [];

      for (const [storedKey, data] of Object.entries(storedIdentities)) {
        if (data.pendingRegistration) {
          continue;
        }
        const canonicalIdentityId = (data.identityId || storedKey) as IdentityId;
        const normalizedUrl = normalizeServerUrl(data.serverUrl);
        const serverKey = serverKeyFromServerUrl(normalizedUrl) || '';
        const fingerprint = data.fingerprint || await computeFingerprint(data.publicKey);

        let signingKey: SigningKey;
        let ecdhKey: CryptoKey | null;
        let ecdhScalar: Uint8Array;
        let vaultKey: CryptoKey;
        let wrappedPrivateKey: WrappedKey;

        if (data.wrappedPrivateKey) {
          // New format: private key is wrapped with localWrappingKey.
          try {
            const rawBytes = await unwrapIdentityPrivateKey(data.wrappedPrivateKey, canonicalIdentityId);
            const publicKeyBytes = base64ToBytes(data.publicKey.value);
            ({ signingKey, ecdhKey, ecdhScalar } = await createDeviceCryptoKeys(rawBytes, publicKeyBytes));
            vaultKey = await deriveVaultKey(rawBytes);
            wrappedPrivateKey = data.wrappedPrivateKey;
          } catch (e) {
            console.error(`[Auth] Identity ${canonicalIdentityId}: failed to unwrap — skipping.`, e);
            continue;
          }
        } else if (data.privateKeyBytes) {
          // MIGRATION: Old format stored raw bytes. Wrap and re-save.
          // This branch can be removed after June 2026 once all identities have migrated.
          try {
            const result = await saveIdentity(
              canonicalIdentityId,
              data.publicKey,
              data.privateKeyBytes,
              {
                serverId: serverKey || data.serverId,
                serverName: data.serverName,
                serverUrl: normalizedUrl,
                role: data.role,
                accessKind: data.accessKind,
                accessContextLabel: data.accessContextLabel,
                vpsId: data.vpsId,
                isServerAdmin: data.isServerAdmin,
                isPrimaryVpsTarget: data.isPrimaryVpsTarget,
                fingerprint,
                identityName: data.identityName,
                publishIdentity: data.publishIdentity,
                presenceVisible: data.presenceVisible,
                noNamesOnServer: data.noNamesOnServer
              }
            );
            ({ signingKey, ecdhKey, ecdhScalar, wrappedPrivateKey } = result);
            vaultKey = await deriveVaultKey(data.privateKeyBytes);
          } catch (e) {
            console.error(`[Auth] Identity ${canonicalIdentityId}: migration failed — skipping.`, e);
            continue;
          }
        } else {
          continue;
        }

        const identity: Identity = {
          identityId: canonicalIdentityId,
          // Prefer URL-derived key; fall back to stored value for older records.
          serverId: serverKey || data.serverId || '',
          serverName: data.serverName || '',
          serverUrl: normalizedUrl,
          identityName: data.identityName,
          publishIdentity: data.publishIdentity,
          presenceVisible: data.presenceVisible,
          noNamesOnServer: data.noNamesOnServer ?? false,
          publicKey: data.publicKey,
          fingerprint,
          role: data.role,
          accessKind: data.accessKind,
          accessContextLabel: data.accessContextLabel,
          vpsId: data.vpsId,
          isServerAdmin: data.isServerAdmin ?? false,
          isPrimaryVpsTarget: data.isPrimaryVpsTarget ?? false,
          signingKey,
          ecdhKey,
          ecdhScalar,
          vaultKey,
          wrappedPrivateKey,
          useForBackupStorage: data.useForBackupStorage ?? false
        };

        identities[canonicalIdentityId] = identity;

        // Legacy migration: some older clients may have stored identities under a different key.
        if (storedKey !== canonicalIdentityId) {
          migrations.push({ fromKey: storedKey, toKey: canonicalIdentityId, identity });
        }
      }

      // Best-effort: migrate legacy identity keys stored under wrong keys.
      for (const m of migrations) {
        try {
          await deleteIdentity(m.fromKey);
        } catch {
          // Non-fatal: cleanup failure
        }
      }

      const storedDevices = await loadAllDevices();
      const devices: Record<DeviceId, Device> = {};
      const storedTemporaryMemberships = await loadAllTemporaryMemberships();
      const temporaryMemberships: Record<string, TemporaryMembership> = {};

      for (const [id, data] of Object.entries(storedDevices)) {
        if (data.pendingRegistration) {
          continue;
        }
        if (data.signingKey && data.wrappedEcdhScalar) {
          // New format: ecdhScalar wrapped with localWrappingKey.
          let ecdhScalar: Uint8Array;
          try {
            ecdhScalar = await unwrapDeviceEcdhScalar(data.wrappedEcdhScalar, data.deviceId);
          } catch (e) {
            console.error(`[Auth] Device ${data.deviceId}: failed to unwrap ecdhScalar — skipping.`, e);
            continue;
          }
          let ecdhKey: CryptoKey | null = null;
          try {
            ecdhKey = await crypto.subtle.importKey('raw', toArrayBuffer(ecdhScalar), { name: 'X25519' }, false, ['deriveBits']);
          } catch {
            // X25519 not supported; noble fallback via ecdhScalar.
          }
            devices[id] = {
              deviceId: data.deviceId,
              identityId: data.identityId,
              publicKey: data.publicKey,
              encryptionPublicKey: data.encryptionPublicKey ?? null,
              label: data.label,
              signingKey: data.signingKey,
            ecdhKey,
            ecdhScalar
          };
        } else if (data.signingKey && data.ecdhScalar) {
          // MIGRATION: ecdhScalar was stored as plaintext bytes. Wrap it now.
          // Remove this branch after June 2026.
          const ecdhScalar = data.ecdhScalar;
          try {
            await migrateDeviceEcdhScalarToWrapped(data.deviceId, ecdhScalar);
          } catch {
            // Non-fatal: continue with plaintext ecdhScalar
          }
          let ecdhKey: CryptoKey | null = null;
          try {
            ecdhKey = await crypto.subtle.importKey('raw', toArrayBuffer(ecdhScalar), { name: 'X25519' }, false, ['deriveBits']);
          } catch {
            // X25519 not supported; noble fallback via ecdhScalar.
          }
            devices[id] = {
              deviceId: data.deviceId,
              identityId: data.identityId,
              publicKey: data.publicKey,
              encryptionPublicKey: data.encryptionPublicKey ?? null,
              label: data.label,
              signingKey: data.signingKey,
            ecdhKey,
            ecdhScalar
          };
        } else if (data.privateKeyBytes) {
          // MIGRATION: Old format stored raw bytes. Convert to CryptoKeys + ecdhScalar.
          // This branch can be removed after June 2026 once all devices have migrated.
          try {
            const { signingKey, ecdhKey, ecdhScalar } = await saveDevice(
              data.deviceId,
              data.identityId,
              data.publicKey,
              data.privateKeyBytes,
              data.label
            );
            devices[id] = {
              deviceId: data.deviceId,
              identityId: data.identityId,
              publicKey: data.publicKey,
              encryptionPublicKey: data.encryptionPublicKey ?? null,
              label: data.label,
              signingKey,
              ecdhKey,
              ecdhScalar
            };
          } catch (e) {
            console.error(`[Auth] Device ${data.deviceId}: migration failed — skipping.`, e);
          }
        }
      }

      const nowIso = Date.now();
      for (const [enrollmentId, membership] of Object.entries(storedTemporaryMemberships)) {
        const expiresAt = new Date(membership.expiresAt).getTime();
        if (!Number.isFinite(expiresAt) || expiresAt <= nowIso) {
          try {
            await deleteTemporaryMembership(enrollmentId);
          } catch {
            // ignore cleanup errors
          }
          continue;
        }
        try {
          const { signingKey, ecdhKey, ecdhScalar } = await createDeviceCryptoKeys(
            membership.privateKeyBytes,
            base64ToBytes(membership.publicKey.value),
            membership.encryptionPrivateKeyBytes
          );
          temporaryMemberships[enrollmentId] = {
            ...membership,
            signingKey,
            ecdhKey,
            ecdhScalar
          };
        } catch (e) {
          console.error(`[Auth] Temporary membership ${membership.enrollmentId}: key derivation failed — skipping.`, e);
        }
      }

      // Select an initial identity/device.
      // IMPORTANT: the client may be hosted on a domain that is NOT any server.
      // Do NOT infer server choice from the client host URL.
      const firstIdentityId = (Object.keys(identities)[0] as IdentityId | undefined) || null;

      let firstDeviceId: DeviceId | null = null;
      if (firstIdentityId) {
        const deviceForIdentity = Object.values(devices).find((d) => d.identityId === firstIdentityId);
        firstDeviceId = deviceForIdentity?.deviceId || null;
      }
      if (!firstDeviceId) {
        firstDeviceId = (Object.keys(devices)[0] as DeviceId | undefined) || null;
      }

      set({
        masterKey,
        hasMasterKey: !!masterKey,
        identities,
        devices,
        currentDeviceId: firstDeviceId,
        temporaryMemberships,
        isLoading: false,
        isInitialized: true
      });
    } catch (error) {
      console.error('[Auth] Unexpected error during initialization:', error);
      set({
        isLoading: false,
        isInitialized: true
      });
    }
  },

  logout: () => {
    set({
      masterKey: null,
      hasMasterKey: false,
      identities: {},
      devices: {},
      currentDeviceId: null,
      temporaryMemberships: {}
    });
  }
}));

/**
 * User has at least one identity configured (even if master key is missing).
 * This is used to allow navigation while gating sensitive operations per-page.
 */
export const selectHasIdentity = (state: AuthState): boolean => {
  return Object.keys(state.identities).length > 0;
};

export const selectHasTemporaryAccess = (state: AuthState): boolean => {
  return Object.keys(state.temporaryMemberships).length > 0;
};

/**
 * Get identity by ID
 */
export const selectIdentityById = (identityId: IdentityId) => (state: AuthState): Identity | null => {
  return state.identities[identityId] || null;
};

/**
 * Get device by identity ID
 */
export const selectDeviceByIdentityId = (identityId: IdentityId) => (state: AuthState): Device | null => {
  // Find device for this identity
  const device = Object.values(state.devices).find(d => d.identityId === identityId);
  return device || null;
};
